import type { SxProps, Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AUTH_MEMBERSHIP_GRADIENT } from "../common/AuthSidePanel/authBackgroundStyles";

/** Hero (video or gradient) — same height on all screen sizes. */
export const WELCOME_HERO_HEIGHT = "83vh";
export const WELCOME_HERO_CTA_HEIGHT = "20vh";

/** Consistent gap between every welcome page section (≥ 1em) */
export const WELCOME_SECTION_GAP = "2em";

/** Spacing inside a section (e.g. button → link) */
export const WELCOME_INNER_GAP = "1em";

/** Lavender panel background (About image panel + desktop CTA strip) */
export const WELCOME_LAVENDER_PANEL_BG = `linear-gradient(135deg, ${alpha("#6f49c9", 0.12)} 0%, ${alpha("#667eea", 0.2)} 100%)`;

/** Vertical stack for content sections below the hero block */
export const welcomePageStackSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: WELCOME_SECTION_GAP,
  width: "100%"
};

/** Video/gradient hero + desktop CTA strip — flush, no gap between them */
export const welcomeHeroGroupSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  width: "100%",
  flexShrink: 0
};

export const welcomeHeroContainerSx: SxProps<Theme> = {
  minHeight: WELCOME_HERO_HEIGHT,
  height: WELCOME_HERO_HEIGHT,
  maxHeight: WELCOME_HERO_HEIGHT,
  width: "100%",
  position: "relative",
  overflow: "hidden",
  flexShrink: 0
};

/** Mobile welcome hero — gradient only (no auth 100vh min-height) */
export const welcomeMobileHeroBackgroundSx: SxProps<Theme> = {
  position: "relative",
  overflow: "hidden",
  background: AUTH_MEMBERSHIP_GRADIENT
};

/** Shared layout for About, Features, and FAQ — natural height, no extra section padding */
export const welcomeSectionSx: SxProps<Theme> = {
  width: "100%",
  bgcolor: "#fff",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  flexShrink: 0,
  position: "relative",
  overflow: "visible"
};

/** Last section — page bottom breathing room */
export const welcomeFaqSectionSx: SxProps<Theme> = {
  ...welcomeSectionSx,
  pb: WELCOME_SECTION_GAP
};

/** CTA strip below hero (lavender panel — mobile + desktop) */
export const welcomeHeroCtaSx: SxProps<Theme> = {
  width: "100%",
  flexShrink: 0,
  minHeight: WELCOME_HERO_CTA_HEIGHT,
  height: WELCOME_HERO_CTA_HEIGHT,
  maxHeight: WELCOME_HERO_CTA_HEIGHT,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  position: "relative",
  overflow: "hidden",
  px: { xs: 2, sm: 3 },
  background: WELCOME_LAVENDER_PANEL_BG
};

/** Subtle decorative gradients inside the CTA strip */
export const welcomeHeroCtaOrb1Sx: SxProps<Theme> = {
  position: "absolute",
  top: "-30%",
  right: "8%",
  width: 140,
  height: 140,
  borderRadius: "50%",
  pointerEvents: "none",
  background: `radial-gradient(circle, ${alpha("#6f49c9", 0.2)} 0%, transparent 72%)`
};

export const welcomeHeroCtaOrb2Sx: SxProps<Theme> = {
  position: "absolute",
  bottom: "-35%",
  left: "6%",
  width: 120,
  height: 120,
  borderRadius: "50%",
  pointerEvents: "none",
  background: `radial-gradient(circle, ${alpha("#667eea", 0.22)} 0%, transparent 70%)`
};

export const welcomeSectionContentSx: SxProps<Theme> = {
  width: { xs: "90%", md: "70%" },
  maxWidth: 1200,
  mx: "auto"
};

export const welcomeSectionEyebrowSx: SxProps<Theme> = {
  color: "primary.main",
  fontWeight: 600,
  fontSize: { xs: "0.8125rem", md: "0.875rem" },
  mb: 1
};

export const welcomeSectionTitleSx: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: { xs: "1.375rem", sm: "1.5rem", md: "1.75rem" },
  lineHeight: 1.3,
  color: "#1a1a2e",
  textTransform: "none",
  mb: 1.5
};

/** Intro line under section titles (Features, FAQ) */
export const welcomeSectionIntroSx: SxProps<Theme> = {
  color: "text.secondary",
  lineHeight: 1.7,
  fontSize: { xs: "0.875rem", md: "0.9375rem" },
  mb: 3
};

export const welcomeSectionBodySx: SxProps<Theme> = {
  color: "text.secondary",
  lineHeight: 1.75,
  fontSize: { xs: "0.875rem", md: "0.9375rem" },
  mb: 3
};

export const welcomeAboutImageSx: SxProps<Theme> = {
  position: "relative",
  borderRadius: { xs: 3, md: "24px 24px 24px 0" },
  overflow: "hidden",
  maxHeight: 280,
  boxShadow: `0 24px 48px ${alpha("#1a0a3e", 0.08)}`
};

export const welcomeFeatureCardSx: SxProps<Theme> = {
  p: 2,
  borderRadius: 2
};

export const welcomeFaqAccordionSx: SxProps<Theme> = {
  "&:before": { display: "none" },
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  mb: 1.5,
  bgcolor: alpha("#6f49c9", 0.06),
  border: `1px solid ${alpha("#6f49c9", 0.12)}`,
  boxShadow: "none",
  overflow: "hidden"
};

export const welcomeFooterMainSx: SxProps<Theme> = {
  width: "100%",
  bgcolor: "#f3f3f3",
  borderTop: "1px solid",
  borderColor: "divider",
  py: { xs: 4, md: 5 }
};

export const welcomeFooterColumnTitleSx: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: { xs: "0.9375rem", md: "1rem" },
  color: "#1a1a2e",
  mb: 1.5
};

export const welcomeFooterLinkSx: SxProps<Theme> = {
  display: "block",
  color: "text.secondary",
  fontSize: { xs: "0.875rem", md: "0.9375rem" },
  lineHeight: 1.8,
  textDecoration: "none",
  "&:hover": {
    color: "primary.main",
    textDecoration: "underline"
  }
};

export const welcomeFooterBottomSx: SxProps<Theme> = {
  width: "100%",
  py: 2,
  textAlign: "center",
  background: WELCOME_LAVENDER_PANEL_BG,
  color: "primary.main",
  fontSize: "0.875rem",
  fontWeight: 500
};
